import { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import useOrders from 'api/useOrders';
import usePermissions from 'api/usePermissions';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import { EOrderStatus } from 'constants/enums';
import { useUserPermissions } from 'hooks/useUserPermissions';
import { getQueryParams } from 'utils/queryParamsUtil';
import { EToasterStatus, showToast } from 'utils/showToast';

import Button from 'components/Button/Button';

import { DisplayPrices, Order } from '../types';

import { RefundModal } from './RefundModal';

export const OrderHeader = ({
  order,
  orderId,
  displayPrices
}: {
  order?: Order;
  orderId: string;
  displayPrices: DisplayPrices | null;
}) => {
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const navigate = useNavigate();
  const publisherId =
    getQueryParams('publisherId') || order?.offerSnapshot?.publisherId || '';
  const { getPermissions } = usePermissions();
  const permissions = getPermissions();
  const { isActionEnabled } = useUserPermissions();
  const { retryAward, getOrderDetails } = useOrders(
    orderId,
    undefined,
    undefined,
    { selectedProjects: publisherId }
  );
  const [isRetrying, setIsRetrying] = useState(false);

  const canRetry =
    order?.status === EOrderStatus.charge_failed &&
    isActionEnabled(
      permissions,
      EPermissionGroup.ORDERS,
      EPermissionAction.AWARD_RETRY,
      publisherId
    );

  const handleRetry = () => {
    if (!order || !publisherId) return;

    setIsRetrying(true);
    retryAward.mutate(
      {
        orderId,
        publisherId
      },
      {
        onSuccess: () => {
          showToast({
            message: 'Award retry initiated successfully',
            status: EToasterStatus.SUCCESS
          });
          getOrderDetails.refetch();
        },
        onError: () => {
          showToast({
            message: 'Failed to run award process',
            status: EToasterStatus.ERROR
          });
        },
        onSettled: () => {
          setIsRetrying(false);
        }
      }
    );
  };

  const canRefund =
    order &&
    (order.status === EOrderStatus.charge_succeed ||
      order.status === EOrderStatus.charge_failed);

  return (
    <>
      {canRefund && (
        <RefundModal
          isOpen={isRefundModalOpen}
          onClose={() => setIsRefundModalOpen(false)}
          totalAmount={displayPrices?.totalPayment ?? '0'}
          currencySymbol={order.currencySymbol}
          orderId={orderId}
          publisherId={publisherId}
        />
      )}
      <div className="flex items-center justify-between ">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="h-7 !w-2"
          >
            <FontAwesomeIcon icon="fa-solid fa-chevron-left !text-xs h-2! w-2!" />
          </Button>
          <h2 className="font-bold text-lg text-base-primary">
            Order ID {orderId}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {canRefund && (
            <Button onClick={() => setIsRefundModalOpen(true)}>Refund</Button>
          )}
          {canRetry && (
            <Button
              onClick={handleRetry}
              disabled={isRetrying}
              variant="outline"
            >
              {isRetrying ? (
                <>
                  <div className="w-4 h-4 border-2 border-base-foreground border-t-transparent rounded-full animate-spin mr-2" />
                  Retrying...
                </>
              ) : (
                <>
                  <FontAwesomeIcon
                    icon="fa-regular fa-rotate-right"
                    className="mr-2"
                  />
                  Retry Award
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </>
  );
};
